(() => {
  const registry = window.CKB_NEC_PATTERN_REGISTRY;
  if (!registry) return;

  const approvals = {
    'lefs-feather': {
      id: 'lefs-feather',
      name: 'CHA LEFS FEATHER',
      family: 'Ultralight QRP end-fed wire',
      productTerms: ['cha lefs feather', 'lefs feather'],
      variables: ['66.5 ft factory radiator', 'guide sloper endpoints', '49:1 terminal boundary', 'support height', 'ground', 'feed-line current'],
      type: 'Wire',
      missions: ['Portable', 'POTA', 'SOTA', 'QRP'],
      hoa: true,
      state: 'published',
      evidence: 'SIMULATION — NORMALIZED PATTERN ONLY, FEED/MATCH/LOSS-UNCORRELATED',
      bands: ['40 m', '20 m', '15 m', '10 m'],
      priority: 'Released',
      batch: 'CHA LEFS FEATHER R001',
      url: '/pages/chameleon-knowledge-base?tool=patterns&target=lefs-feather#ckb-pattern-library',
      engineering: {
        releaseCandidate: '2026-09-02-R001/R002',
        nativeRuns: 30,
        primaryModels: 14,
        convergedModels: 14,
        customerReadyModels: 4,
        rejectedModels: 0,
        review: 'Four product operating-band cases at the documented 25 ft far-end height are customer-selectable. Tuner-assisted study cases and seven 20 ft historical runs remain internal evidence.',
        publication: 'APPROVED FOR BOUNDED NORMALIZED PATTERN EDUCATION — NOT AN INSTALLED SWR, EFFICIENCY, REALIZED-GAIN, RANGE, TRANSFORMER, FEED-LINE OR FIELD-CORRELATION CLAIM'
      }
    },
    'td-lite': {
      id: 'td-lite',
      name: 'CHA TD Lite 2.0',
      family: 'Terminated tactical HF dipole',
      productTerms: ['cha td lite 2.0', 'td lite'],
      variables: ['documented radiator geometry', 'termination', 'support height', 'ground', 'feed boundary'],
      type: 'Dipole',
      missions: ['EMCOMM', 'Tactical', 'Portable'],
      hoa: true,
      state: 'published',
      evidence: 'SIMULATION — NORMALIZED PATTERN ONLY, FEED/MATCH/LOSS-UNCORRELATED',
      bands: ['160 m', '80 m', '60 m', '40 m', '30 m', '20 m', '17 m', '15 m', '12 m', '10 m', '6 m'],
      priority: 'Released',
      batch: 'CHA TD Lite 2.0 R001/R002',
      url: '/pages/chameleon-knowledge-base?tool=patterns&target=td-lite#ckb-pattern-library',
      engineering: {
        releaseCandidate: '2026-09-02-R001/R002',
        nativeRuns: 88,
        primaryModels: 44,
        convergedModels: 44,
        customerReadyModels: 44,
        rejectedModels: 0,
        review: 'All forty-four product-specific primary/fine pairs passed the established numerical and asset gates.',
        publication: 'APPROVED FOR BOUNDED NORMALIZED PATTERN EDUCATION — NOT AN INSTALLED SWR, EFFICIENCY, REALIZED-GAIN, RANGE, MATCHING-NETWORK, FEED-LINE OR FIELD-CORRELATION CLAIM'
      }
    },
    floop3: {
      id: 'floop3',
      name: 'CHA F-LOOP 3.0',
      family: 'Manually tuned portable magnetic loop',
      productTerms: ['cha f-loop 3.0', 'f loop 3'],
      variables: ['selected loop radiator', 'coupling loop', 'tuning state', 'height', 'ground', 'nearby structures'],
      type: 'Loop',
      missions: ['Portable', 'POTA', 'HOA', 'Field'],
      hoa: true,
      state: 'published',
      evidence: 'SIMULATION — NORMALIZED TUNED-LOOP PATTERN ONLY, FEED/MATCH/LOSS-UNCORRELATED',
      bands: ['80 m', '60 m', '40 m', '30 m', '20 m', '17 m', '15 m', '12 m', '10 m'],
      priority: 'Released',
      batch: 'CHA F-LOOP 3.0 R003/R007',
      url: '/pages/chameleon-knowledge-base?tool=patterns&target=floop3#ckb-pattern-library',
      engineering: {
        releaseCandidate: '2026-09-02-R003/R007',
        nativeRuns: 46,
        primaryModels: 23,
        convergedModels: 23,
        customerReadyModels: 23,
        rejectedModels: 0,
        review: 'All twenty-three released primary/fine pairs passed the established numerical and asset gates. The 80 m double-flexible case uses the approved R007 bounded surrogate.',
        publication: 'APPROVED FOR BOUNDED NORMALIZED PATTERN EDUCATION — NOT AN INSTALLED SWR, EFFICIENCY, REALIZED-GAIN, RANGE, TUNING, LOSS OR FIELD-CORRELATION CLAIM'
      }
    }
  };

  for (const [id, approval] of Object.entries(approvals)) {
    const record = registry.byId(id);
    if (record) Object.assign(record, approval);
    else registry.configurations.push({ ...approval });
  }

  registry.release = '2.1.0';
  window.dispatchEvent(new CustomEvent('chameleon:pattern-registry-ready', {
    detail: { release: registry.release, approvedFamilies: Object.keys(approvals) }
  }));
  const familyDefinitions = {
    'lefs-feather': {
      modelPrefix: 'CHA-LEFS-FEATHER-',
      title: 'Choose the FEATHER sloper height and band',
      copy: 'Each option preserves the 66½ ft final production radiator, approximately 25 ft far-end height, terminal boundary, ground and frequency.'
    },
    'td-lite': {
      modelPrefix: 'CHA-TD-LITE-2-',
      title: 'Choose the TD Lite 2.0 geometry and band',
      copy: 'Each option preserves the documented terminated-wire geometry, 25 ft support reference, ground, termination boundary and frequency.'
    },
    floop3: {
      modelPrefix: 'CHA-F-LOOP-3-',
      title: 'Choose the F-LOOP 3.0 configuration and band',
      copy: 'Each option preserves the documented loop geometry. The 80 m configuration uses the two adjacent, no-gap ABR Industries LMR-400 coax lengths formed as the guide-defined coil.'
    }
  };
  const esc = value => String(value || '').replace(/[&<>"']/g, character => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' })[character]);

  const enhanceRecord = async record => {
    const family = familyDefinitions[record?.dataset.patternRecord];
    if (!family || record.querySelector('[data-approved-family-lab]') || !window.CHA_NEC2D_POLAR || !window.CKB_NEC3D) return;
    const actions = record.querySelector('.ckb-pattern-record__actions');
    if (!actions) return;
    actions.querySelector(`a[href*="target=${record.dataset.patternRecord}"]`)?.remove();
    actions.insertAdjacentHTML('beforebegin', `<section class="ckb-bv-lab" data-approved-family-lab><header class="ckb-bv-lab__intro"><div><small>Product-specific NEC-4.2 pattern library</small><h4>${esc(family.title)}</h4><p>${esc(family.copy)}</p></div><span>SIMULATION — NORMALIZED PATTERN SHAPE</span></header><div class="ckb-reviewed-config"><span>Choose a band and configuration</span><div class="ckb-reviewed-config__buttons" data-approved-family-buttons role="group" aria-label="Reviewed band and configuration"></div><label class="visually-hidden">Reviewed configuration<select data-approved-family-select tabindex="-1" aria-hidden="true"><option value="">Loading reviewed configurations…</option></select></label></div><div data-approved-family-result><aside class="ckb-pattern-record__pending"><strong>Loading the governed 2D and 3D evidence…</strong><p>No neighboring antenna or decorative curve will be substituted.</p></aside></div></section>`);
    const lab = record.querySelector('[data-approved-family-lab]');
    const select = lab.querySelector('[data-approved-family-select]');
    const output = lab.querySelector('[data-approved-family-result]');
    const buttons = lab.querySelector('[data-approved-family-buttons]');
    try {
      const library = await window.CHA_NEC2D_POLAR.load();
      const cases = library.configs.filter(item => {
        if (!item.modelId?.startsWith(family.modelPrefix) || !String(item.publicationGate || '').includes('APPROVED')) return false;
        if (record.dataset.patternRecord === 'lefs-feather') return /SLOPER-25FT/i.test(String(item.modelId || '')) && /-(40M|20M|15M|10M)-/i.test(String(item.modelId || ''));
        return true;
      }).sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric:true }));
      if (!cases.length) throw new Error('No approved family configurations');
      const choiceLabel = item => {
        if (record.dataset.patternRecord === 'lefs-feather') {
          const band = String(item.modelId || '').match(/-(40M|20M|15M|10M)-/i)?.[1];
          if (band) return band.toUpperCase();
        }
        return item.label.replace(/^(?:CHA TD Lite 2\.0|CHA F-LOOP 3\.0|CHA LEFS Feather)\s*[·-]?\s*/i, '').replace(/\s+/g, ' ').trim();
      };
      select.innerHTML = cases.map(item => `<option value="${esc(item.id)}">${esc(choiceLabel(item))}</option>`).join('');
      buttons.innerHTML = cases.map((item,index) => `<button type="button" data-approved-family-choice="${esc(item.id)}" aria-pressed="${index===0?'true':'false'}">${esc(choiceLabel(item))}</button>`).join('');
      const render = async () => {
        const item = cases.find(test => test.id === select.value) || cases[0];
        select.value = item.id;
        buttons.querySelectorAll('[data-approved-family-choice]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.approvedFamilyChoice === item.id)));
        output.innerHTML = `<article class="ckb-bv-lab__case" data-nec2d-enhanced="true"><div class="ckb-bv-lab__identity"><div><h4>${esc(item.label)}</h4><p>${esc(item.frequencyMHz)} MHz · documented reference configuration</p></div><dl><div><dt>Evidence</dt><dd>NEC-4.2 simulation</dd></div><div><dt>Display</dt><dd>Paired 2D + interactive 3D</dd></div><div><dt>Scope</dt><dd>Normalized pattern</dd></div></dl></div><figure class="cha-nec2d-polar" data-nec2d-polar data-nec2d-model="${esc(item.modelId)}"><div class="cha-nec2d-polar__stage"><p class="cha-nec2d-polar__status" data-nec2d-status>Loading governed 2D NEC pattern…</p></div><figcaption><strong>Paired 2D NEC pattern</strong><span>Azimuth and elevation cuts for this exact governed configuration.</span></figcaption></figure><div class="ckb-bv-lab__graphics"><figure class="ckb-nec3d-inline" data-nec3d-inline data-nec3d-state="ready"><div class="ckb-nec3d-inline__stage"><canvas tabindex="0" aria-label="Interactive normalized three-dimensional NEC radiation pattern. Drag to rotate, scroll or pinch to zoom, or use the arrow keys."></canvas><span data-approved-family-3d-status>Loading governed 3D NEC pattern…</span></div><figcaption><strong>Interactive 3D pattern</strong><button type="button" data-nec3d-open data-nec3d-model="${esc(item.modelId)}" disabled>Open larger</button></figcaption></figure></div><div class="ckb-bv-lab__meaning"><article><small>Evidence boundary</small><p>${esc(item.scope)}</p></article><article><small>What is shown</small><p>${esc(item.evidence)}</p></article><article><small>Your field action</small><p>Match this exact geometry, height, feed and RF-return boundary before applying its directional pattern to a station plan.</p></article></div></article>`;
        await window.CHA_NEC2D_POLAR.renderNode(output.querySelector('[data-nec2d-polar]'), library, item);
        const canvas = output.querySelector('.ckb-nec3d-inline canvas');
        const status = output.querySelector('[data-approved-family-3d-status]');
        status.setAttribute('data-nec3d-inline-status', '');
        const open = output.querySelector('[data-nec3d-open]');
        try {
          await window.CKB_NEC3D.mountInline(canvas, item.modelId);
          status.textContent = 'Drag to rotate · scroll or pinch to zoom';
          open.disabled = false;
        } catch (error) {
          status.dataset.nec3dError = String(error?.message || error || 'Unknown governed 3D loading error');
          status.textContent = 'The governed 3D source is unavailable; no substitute pattern is shown.';
          open.remove();
        }
      };
      select.addEventListener('change', render);
      buttons.addEventListener('click', event => { const button=event.target.closest('[data-approved-family-choice]'); if(!button)return; select.value=button.dataset.approvedFamilyChoice; render(); });
      await render();
    } catch (_) {
      output.innerHTML = '<aside class="ckb-pattern-record__pending"><strong>The approved product-specific NEC library did not load.</strong><p>No substitute pattern is shown. Reload the page or use Chameleon technical support.</p></aside>';
    }
  };

  let dependencyRetry = 0;
  let dependencyRetryTimer = null;
  const scan = () => {
    const records = [...document.querySelectorAll('[data-pattern-record]')];
    records.forEach(enhanceRecord);
    const hasPendingApprovedFamily = records.some(record => familyDefinitions[record.dataset.patternRecord] && !record.querySelector('[data-approved-family-lab]'));
    if (hasPendingApprovedFamily && dependencyRetry < 50) {
      clearTimeout(dependencyRetryTimer);
      dependencyRetry += 1;
      dependencyRetryTimer = setTimeout(scan, 200);
    }
  };
  const start = () => {
    const result = document.querySelector('[data-pattern-library-result]');
    if (result && !result.dataset.threeFamilyLabObserved) {
      new MutationObserver(scan).observe(result, { childList:true, subtree:true });
      result.dataset.threeFamilyLabObserved = 'true';
    }
    scan();
  };
  window.addEventListener?.('chameleon:pattern-library-ready', start);
  window.addEventListener?.('ckb:nec3d-ready', start);
  if (window.CKB_PATTERN_LIBRARY || window.CKB_NEC3D) start();
})();
